import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronDown, ChevronRight, LockKeyhole, LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type { BreadcrumbItem } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import styles from './Topbar.module.css';
import { routes } from '@/lib/routePaths';

interface TopbarProps {
  onDrawerToggle: () => void;
  drawerOpen: boolean;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  activePath: string;
  breadcrumbs: BreadcrumbItem[];
  onLogout: () => void;
}

export default function Topbar({
  onDrawerToggle,
  drawerOpen,
  sidebarCollapsed,
  onToggleSidebar,
  activePath,
  breadcrumbs,
  onLogout,
}: TopbarProps) {
  const { user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setUserMenuOpen(false), [activePath]);

  useEffect(() => {
    if (!userMenuOpen) return;
    userMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    function closeOnOutsideClick(event: MouseEvent) {
      if (!userMenuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
        userMenuTriggerRef.current?.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const items = Array.from(userMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
      if (!items.length) return;
      event.preventDefault();
      const current = items.indexOf(document.activeElement as HTMLElement);
      if (event.key === 'Home') items[0].focus();
      else if (event.key === 'End') items.at(-1)?.focus();
      else if (event.key === 'ArrowDown') items[(current + 1 + items.length) % items.length].focus();
      else items[(current - 1 + items.length) % items.length].focus();
    }
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [userMenuOpen]);

  return (
    <header className={styles.topbar}>
      <button
        className={styles.sidebarToggle}
        onClick={onToggleSidebar}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
      </button>

      <Link to="/" className={styles.topbarBrand}>MegaTraderDX</Link>

      <nav className={styles.topbarBreadcrumb} aria-label="Breadcrumb">
        {breadcrumbs.map((item, index) => (
          <span className={styles.breadcrumbPart} key={`${item.label}-${index}`}>
            {index > 0 && <ChevronRight className={styles.breadcrumbSeparator} size={14} />}
            {item.path ? (
              <Link to={item.path} className={styles.breadcrumbItem}>{item.label}</Link>
            ) : (
              <span className={`${styles.breadcrumbItem} ${styles.breadcrumbItemActive}`} aria-current="page">
                {item.label}
              </span>
            )}
          </span>
        ))}
      </nav>

      <button
        className={styles.drawerToggle}
        onClick={onDrawerToggle}
        aria-label="Open navigation menu"
        aria-expanded={drawerOpen}
        aria-controls="mobile-navigation-drawer"
        aria-haspopup="dialog"
      >
        <Menu size={18} />
      </button>

      <div className={styles.userMenu} ref={userMenuRef}>
        <button
          ref={userMenuTriggerRef}
          type="button"
          className={styles.userMenuTrigger}
          aria-expanded={userMenuOpen}
          aria-haspopup="menu"
          onClick={() => setUserMenuOpen((open) => !open)}
        >
          <span>{user?.displayName}</span>
          <ChevronDown size={14} />
        </button>
        {userMenuOpen && <div className={styles.userMenuPanel} role="menu">
          <strong>{user?.displayName}</strong>
          <span className={styles.userEmail}>{user?.email}</span>
          <div className={styles.userMenuDivider} role="separator" />
          <Link className={styles.userMenuAction} role="menuitem" to={routes.myAlerts}><Bell size={14} /> My alerts</Link>
          <Link className={styles.userMenuAction} role="menuitem" to={routes.security}><LockKeyhole size={14} /> Security</Link>
          <button
            className={styles.userMenuAction}
            role="menuitem"
            type="button"
            onClick={() => {
              setUserMenuOpen(false);
              onLogout();
            }}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>}
      </div>
    </header>
  );
}
